/// <reference path="koyori.ts" />
/// <reference path="fetch.ts" />
/// <reference path="dialog.ts" />
/// <reference path="form.ts" />
/// <reference path="data_grid.ts" />
/// <reference path="button.ts" />

/**
 * データの管理コンポーネントを作成します。
 * <dl>
 * <dt>属性</dt>
 * <dd>label: タイトル文字列です</dd>
 * <dd>columns: 表示するプロパティの名称をカンマ区切りで指定します</dd>
 * <dd>select-url: 検索条件を送信するURLです</dd>
 * <dd>insert-url: 追加情報を送信するURLです</dd>
 * <dd>update-url: 更新情報を送信するURLです</dd>
 * <dd>delete-url: 削除情報を送信するURLです</dd>
 * <dd>update-multi-url: 複数の更新加情報を送信するURLです</dd>
 * <dd>delete-multi-url: 複数の削除情報を送信するURLです</dd>
 * <dd>insert-method: 追加情報を送信するHTTPメソッドです（デフォルト: POST）</dd>
 * <dd>update-method: 追加情報を送信するHTTPメソッドです（デフォルト: PUT）</dd>
 * <dd>delete-method: 追加情報を送信するHTTPメソッドです（デフォルト: DELETE）</dd>
 * <dd>select-dialog: 検索ダイアログを別途指定する場合のダイアログ（koy-dialogタグ）のセレクタです</dd>
 * <dd>insert-dialog: 追加ダイアログを別途指定する場合のダイアログ（koy-dialogタグ）のセレクタです</dd>
 * <dd>update-dialog: 更新ダイアログを別途指定する場合のダイアログ（koy-dialogタグ）のセレクタです</dd>
 * <dd>update-multi-dialog: 一括更新ダイアログを別途指定する場合のダイアログ（koy-dialogタグ）のセレクタです</dd>
 * <dd>definition: 入力項目の定義をJSON形式で指定します</dd>
 * <dd>definition-url: 入力項目の定義を取得するURLです</dd>
 * <dd>keys: definitionやdefinition-urlが指定されていない場合に行削除処理を行う場合のキーをカンマ区切りで指定します</dd>
 * <dd>datagrid: データグリッドを別途指定する場合のデータグリッド（koy-datagridタグ）のセレクタです</dd>
 * <dd>selected: チェックボックスがチェックされている場合に設定されます</dd>
 * <dd>use-query: URLのクエリを初期検索パラメータとして使用します</dd>
 * </dl>
 * <dl>
 * <dt>イベント</dt>
 * <dd>insert-success: 追加が成功した場合</dd>
 * <dd>insert-error: 追加に失敗した場合</dd>
 * <dd>update-success: 更新が成功した場合</dd>
 * <dd>update-error: 更新に失敗した場合</dd>
 * <dd>delete-success: 削除が成功した場合</dd>
 * <dd>delete-error: 削除に失敗した場合</dd>
 * <dd>row-append: 行が追加された場合</dd>
 * </dl>
 */
class KoyoriManager extends KoyoriElement {

	/** リサイズ用タイマ */
	private resizeTimer: NodeJS.Timer;

	/**
	 * @param name フィルタ名
	 * @return フィルタ
	 */
	static getFilterByName(filters: KoyoriFormDefinitionTypeProperty[], name: string): KoyoriFormDefinitionTypeProperty {
		let result = null;
		filters.forEach(type => {
			if (type.name == name) {
				result = type;
			}
		});
		return result;
	}

	private selectDialog: KoyoriDialog;

	private insertDialog: KoyoriDialog;

	private updateDialog: KoyoriDialog;

	private updateMultiDialog: KoyoriDialog;

	/** ヘッダ */
	private header: HTMLElement;

	/** 選択状態のラベル */
	private selectedLabel: HTMLElement;

	/** データグリッド */
	private datagrid: KoyoriDatagrid;

	/** 定義情報 */
	private definition: KoyoriManagerDefinition;

	constructor() {
		super();
	}

	connectedCallback(): void {
		super.connectedCallback();

		if (this.querySelector('.koy-manager-default-header') == null) {
			this.header = document.createElement('div');
			this.header.classList.add('koy-manager-header');
			{
				let title = document.createElement('span');
				title.textContent = this.getAttribute('label') || '';
				title.classList.add('koy-manager-title');
				this.header.appendChild(title);

				this.selectedLabel = document.createElement('span');
				this.selectedLabel.classList.add('koy-manager-selected-label');
				this.selectedLabel.textContent = 'test';
				this.header.appendChild(this.selectedLabel);

				let select = new KoyoriButton();
				select.classList.add('koy-manager-select');
				select.setAttribute('title', '絞込');
				select.addEventListener('click', () => {
					this.selectDialog.open();
				});
				this.header.appendChild(select);

				let insert = new KoyoriButton();
				insert.classList.add('koy-manager-insert');
				insert.setAttribute('title', '追加');
				insert.addEventListener('click', () => {
					this.insertDialog.open();
				});
				this.header.appendChild(insert);

				let update = new KoyoriButton();
				update.classList.add('koy-manager-multi-update');
				update.setAttribute('title', '一括更新');
				update.addEventListener('click', () => {
					this.updateMultiDialog.open();
				});
				this.header.appendChild(update);

				let remove = new KoyoriButton();
				remove.classList.add('koy-manager-multi-delete');
				remove.setAttribute('title', '一括削除');
				remove.addEventListener('click', () => {
					KoyoriDialog.confirm('選択されている' + this.datagrid.getSelectedRows().length + '行を削除してよろしいですか？', (result, input, dialog) => {
						if (result == false) {
							dialog.close();
							return true;
						}
						dialog.getForm().clearAllMessages();
						this.sendDelete(this.datagrid.getSelectedRows(), this.deleteMultiUrl, dialog, true);
						return false;
					});
				});
				this.header.appendChild(remove);
			}
			this.appendChild(this.header);
		}

		let def = this.getAttribute('definition');
		let defUrl = this.getAttribute('definition-url');
		if (def != null) {
			this.definition = JSON.parse(def);
			this.createDialogs();
			this.createDatagrid();
			if (this.definition.filters == undefined || this.definition.filters.length == 0) {
				this.querySelector('.koy-manager-select').classList.add('koy-hide');
			}
		} else if (defUrl != null) {
			KoyoriFetch.fetch(defUrl, null, {
				success: response => {
					response.json().then(data => {
						if (data == null) {
							return;
						}
						this.definition = data;
						this.createDialogs();
						this.createDatagrid();
						if (this.definition.filters == undefined || this.definition.filters.length == 0) {
							this.querySelector('.koy-manager-select').classList.add('koy-hide');
						}
					});
					return true;
				}
			}, this);
		} else {
			this.createDialogs();
			this.createDatagrid();
			if (this.selectDialog == null) {
				this.querySelector('.koy-manager-select').classList.add('koy-hide');
			}
		}

		window.addEventListener("resize", () => {
			if (this.resizeTimer != undefined) {
				clearTimeout(this.resizeTimer);
			}
			this.resizeTimer = setTimeout(() => {
				this.reLayout()
				this.resizeTimer = undefined;
			}, 100);
		});

		this.reLayout();
	}

	/**
	 * データグリッドを作成します。
	 */
	private createDatagrid(): void {
		if (this.getAttribute('datagrid') != null) {
			this.datagrid = this.querySelector(this.getAttribute('datagrid'));
		} else {
			this.datagrid = new KoyoriDatagrid();
		}
		let url = this.selectUrl;
		if (this.selectDialog) {
			let params = this.selectDialog.getForm().getValuesAsObject();
			for (let key in params) {
				if (url.indexOf('?') < 0) {
					url += '?';
				} else {
					url += '&';
				}
				url += encodeURIComponent(key) + '=' + encodeURIComponent(params[key]);
			}
		}
		this.datagrid.setAttribute('url', url);
		if (this.definition != null) {
			let rootType = KoyoriForm.getTypeByName(this.definition.types, this.definition.type);
			this.getAttribute('columns').split(',').forEach(name => {
				let property = KoyoriForm.getPropertyByName(this.definition, rootType.properties, name);
				if (property == null) {
					return;
				}
				let column = new KoyoriDatagridColumn();
				column.setAttribute('label', property.label);
				column.setAttribute('name', name);
				if (property.candidates != undefined) {
					let labels: any = {};
					property.candidates.forEach(candidate => {
						labels[candidate.value] = candidate.label;
					});
					column.setAttribute('labels', JSON.stringify(labels));
				}
				switch (property.type) {
					case 'number':
						column.setAttribute('type', 'number');
						break;
					case 'date':
						column.setAttribute('type', 'date');
						break;
					case 'time':
						column.setAttribute('type', 'time');
						break;
					case 'datetime':
						column.setAttribute('type', 'datetime');
						break;
				}
				if (!property.multiLine) {
					column.setAttribute('sortable', '');
				}
				this.datagrid.appendChild(column);
			});
			if (this.updateUrl != null || this.deleteUrl != null) {
				let column = new KoyoriDatagridColumn();
				column.setAttribute('type', 'button');
				this.datagrid.appendChild(column);
			}
		}
		{
			let column = this.datagrid.querySelector('koy-datagrid-column[type="button"]:last-of-type');
			let content = new KoyoriDatagridContent();
			if (this.updateUrl != null) {
				let button = new KoyoriButton();
				button.classList.add('koy-manager-update');
				button.setAttribute('title', '更新');
				content.appendChild(button);
			}
			if (this.deleteUrl != null) {
				let button = new KoyoriButton();
				button.classList.add('koy-manager-delete');
				button.setAttribute('title', '削除');
				content.appendChild(button);
			}
			column.appendChild(content);
		}
		this.datagrid.addEventListener('check', event => {
			let rows = <HTMLElement[]>(<CustomEvent>event).detail;
			if (rows.length > 0) {
				this.selectedLabel.textContent = rows.length + '行が選択されています';
				this.setAttribute('selected', '');
			} else {
				this.removeAttribute('selected');
			}
		});
		this.datagrid.addEventListener('click', event => {
			let target = <HTMLElement>event.target;
			let button = target.closest('koy-button');
			if (button == null) {
				return;
			}
			let classes = button.classList;
			if (classes.contains('koy-manager-update')) {
				this.updateDialog.setAttribute('data-row', target.closest('tr').getAttribute('data-row'));
				this.updateDialog.open();
			} else if (classes.contains('koy-manager-delete')) {
				KoyoriDialog.confirm('対象の行を削除してよろしいですか？', (result, input, dialog) => {
					if (result == false) {
						dialog.close();
						return true;
					}
					dialog.getForm().clearAllMessages();
					this.sendDelete([<HTMLElement>target.closest('tr')], this.deleteUrl, dialog, false);
					return false;
				});
			}
		});
		if (this.deleteMultiUrl != null || this.updateMultiUrl != null) {
			this.datagrid.setAttribute('selectable', '');
		}
		this.appendChild(this.datagrid);
	}

	/**
	 * ダイアログを作成します。
	 */
	private createDialogs(): void {
		if (this.selectUrl != null) {
			if (this.getAttribute('select-dialog') != null) {
				this.selectDialog = this.querySelector(this.getAttribute('select-dialog'));
				this.updateCustomDialog(this.selectDialog);
			} else if (this.definition) {
				this.selectDialog = this.createDialog(this.definition, '絞り込み', false, true);
			}
			if (this.selectDialog) {
				this.selectDialog.addEventListener('open', () => {
					this.selectDialog.focusFirstInput();
				});
				this.selectDialog.addEventListener('submit', event => {
					event.preventDefault();
					let form = this.selectDialog.querySelector('koy-form');
					this.select((<KoyoriForm>form).getValuesAsObject());
					this.selectDialog.close();
				});
				this.appendChild(this.selectDialog);

				if (this.getAttribute('use-query') != null) {
					this.selectDialog.getForm().setValuesAsQueryString(location.search);
				}
			}
		}
		if (this.insertUrl != null) {
			if (this.getAttribute('insert-dialog') != null) {
				this.insertDialog = this.querySelector(this.getAttribute('insert-dialog'));
				this.updateCustomDialog(this.insertDialog);
			} else {
				this.insertDialog = this.createDialog(this.definition, '追加');
			}
			this.insertDialog.addEventListener('open-pre', () => {
				this.insertDialog.getForm().reset();
			});
			this.insertDialog.addEventListener('open', () => {
				this.insertDialog.focusFirstInput();
			});
			this.insertDialog.addEventListener('submit', event => {
				event.preventDefault();
				let json = this.insertDialog.getForm().getValuesAsJson();
				this.callApi(json, this.insertUrl, this.insertMethod, 'insert', this.insertDialog, response => {
					this.datagrid.refresh();
					return true;
				});
			});
			this.appendChild(this.insertDialog);
		}
		if (this.updateUrl != null) {
			if (this.getAttribute('update-dialog') != null) {
				this.updateDialog = this.querySelector(this.getAttribute('update-dialog'));
				this.updateCustomDialog(this.updateDialog);
			} else {
				this.updateDialog = this.createDialog(this.definition, '更新', true);
			}
			this.updateDialog.addEventListener('open-pre', () => {
				this.updateDialog.getForm().reset();
				let row = JSON.parse(this.updateDialog.getAttribute('data-row') || '{}');
				this.updateDialog.getForm().setValues(row);
			});
			this.updateDialog.addEventListener('open', () => {
				this.updateDialog.focusFirstInput();
			});
			this.updateDialog.addEventListener('submit', event => {
				event.preventDefault();
				let json = this.updateDialog.getForm().getValuesAsJson();
				this.callApi(json, this.updateUrl, this.updateMethod, 'update', this.updateDialog, response => {
					this.datagrid.refresh();
					return true;
				});
			});
			this.appendChild(this.updateDialog);
		}
		if (this.updateMultiUrl != null) {
			if (this.getAttribute('update-multi-dialog') != null) {
				this.updateMultiDialog = this.querySelector(this.getAttribute('update-multi-dialog'));
			} else {
				this.updateMultiDialog = this.createDialog(this.definition, '一括更新', true);
			}
			this.updateMultiDialog.addEventListener('open-pre', () => {
				this.updateMultiDialog.getForm().reset();
			});
			this.updateMultiDialog.addEventListener('open', () => {
				this.updateMultiDialog.focusFirstInput();
			});
			this.updateMultiDialog.addEventListener('submit', event => {
				// TODO 送信処理
				event.preventDefault();
				KoyoriFetch.fetch(this.updateMultiUrl, null, {

				}, this);
			});
			this.appendChild(this.updateMultiDialog);
		}
	}

	private createDialog(def: KoyoriManagerDefinition, titlePostfix: string, insertOnlyDisable = false, filter = false): KoyoriDialog {
		let dialog = new KoyoriDialog();
		let content = new KoyoriDialogContent();
		let form = new KoyoriForm();

		let title = document.createElement('h2');
		title.textContent = def.label + 'の' + titlePostfix;
		content.appendChild(title);

		let cancelButton = new KoyoriButton();
		cancelButton.textContent = 'キャンセル';
		cancelButton.classList.add('koy-manager-cancel');
		cancelButton.addEventListener('click', event => {
			dialog.close();
			event.preventDefault();
		});
		form.appendChild(cancelButton);

		let submitButton = new KoyoriButton();
		submitButton.textContent = '決定';
		submitButton.setAttribute('state', 'active');
		submitButton.classList.add('koy-manager-submit');
		submitButton.addEventListener('click', event => {
			KoyoriElement.fireEvent(dialog, 'submit', true, true);
			event.preventDefault();
		});
		form.appendChild(submitButton);

		if (insertOnlyDisable) {
			form.setAttribute('insert-only-disable', '');
		}

		if (filter) {
			let clone = <KoyoriManagerDefinition>JSON.parse(JSON.stringify(def));
			clone.type = 'KoyoriManagerFilters';
			clone.types.push({
				name: "KoyoriManagerFilters",
				properties: clone.filters
			});
			form.definition = JSON.stringify(clone);
		} else {
			form.definition = JSON.stringify(def);
		}
		content.appendChild(form);

		dialog.appendChild(content);
		return dialog;
	}

	/**
	 * ユーザが作成したダイアログにイベントを関連付けます。
	 */
	private updateCustomDialog(dialog: KoyoriDialog): void {
		let cancelButton = dialog.querySelector('.koy-manager-cancel');
		if (cancelButton != null) {
			cancelButton.addEventListener('click', event => {
				dialog.close();
				event.preventDefault();
			});
		}

		let submitButton = dialog.querySelector('.koy-manager-submit');
		if (submitButton != null) {
			submitButton.addEventListener('click', event => {
				KoyoriElement.fireEvent(dialog, 'submit', true, true);
				event.preventDefault();
			});
		}
	}

	/**
	 * 表のレイアウトを調整します。
	 */
	reLayout(): void {
		let parent = this.parentElement;
		parent.style.overflowY = 'hidden';

		let height = KoyoriElement.getFitHeight(this);
		this.style.height = height + "px";

		if (this.datagrid) {
			this.datagrid.reLayout(true);
		}
	}

	/**
	 * APIを呼び出します。
	 * 
	 * @param json
	 * @param url
	 * @param method
	 * @param name 操作名
	 * @param dialog
	 * @param callback 正常に処理が完了した際に呼び出される関数
	 */
	private callApi(json: string, url: string, method: string, name: string, dialog: KoyoriDialog, callback: (response: any) => void): void {
		KoyoriFetch.fetch(url, {
			method: method,
			headers: {
				'Content-Type': 'application/json'
			},
			body: json,
		}, {
			success: response => {
				let result = true;
				response.json().then(data => {
					if (data == null) {
						return;
					}
					if (data.RESPONSE != undefined && data.RESPONSE != 200) {
						KoyoriElement.fireEvent(this, name + '-error', true, true, {
							'status': data.RESPONSE,
							'response': response
						});
						result = false;
						return;
					}
					callback(data);
					KoyoriElement.fireEvent(this, name + '-success', true, true, {
						'status': data.RESPONSE,
						'response': response
					});
					dialog.close();
				})
				return result;
			},
			ng: response => {
				let responseContentType = response.headers.get("Content-Type");
				if (response.status == 400 && responseContentType != null && responseContentType.indexOf('json') > 0) {
					response.json().then(data => {
						for (let name in data) {
							dialog.getForm().addErrorMessage(name, data[name]);
						}
					});
				}
				KoyoriElement.fireEvent(this, 'fetch-ng', true, true, {
					'status': response.status,
					'response': response
				});
				return false;
			},
			complete: () => {
				return true;
			}
		}, this);
	}

	/**
	 * 検索処理を行います。
	 * 
	 * @param params 検索条件のオブジェクト
	 */
	select(params: any) {
		let url = this.selectUrl;
		if (url == null) {
			return;
		}
		for (let key in params) {
			if (url.indexOf('?') < 0) {
				url += '?';
			} else {
				url += '&';
			}
			url += encodeURIComponent(key) + '=' + encodeURIComponent(params[key]);
		}
		this.datagrid.setAttribute('url', url);
		this.datagrid.refresh();
	}

	/**
	 * 削除APIを呼び出します。
	 * 
	 * @param selectedRows
	 * @param url
	 * @param dialog
	 */
	private sendDelete(selectedRows: HTMLElement[], url: string, dialog: KoyoriDialog, multi: boolean): void {
		if (multi) {
			let body: any[] = [];
			selectedRows.forEach(row => {
				let data = JSON.parse(row.getAttribute('data-row'));
				let keys: any = {};
				if (this.definition != null) {
					this.definition.keys.forEach(key => {
						keys[key] = data[key];
					});
				} else {
					this.getAttribute('keys').split(',').forEach(key => {
						keys[key] = data[key];
					});
				}
				body.push(keys);
			});
			this.callApi(JSON.stringify(body), url, this.deleteMethod, 'delete', dialog, response => {
				this.datagrid.deleteRows(selectedRows);
			});
		} else {
			let row = selectedRows[0];
			let data = JSON.parse(row.getAttribute('data-row'));
			let keys: any = {};
			if (this.definition != null) {
				this.definition.keys.forEach(key => {
					keys[key] = data[key];
				});
			} else {
				this.getAttribute('keys').split(',').forEach(key => {
					keys[key] = data[key];
				});
			}
			let body = keys;
			this.callApi(JSON.stringify(body), url, this.deleteMethod, 'delete', dialog, response => {
				this.datagrid.deleteRows(selectedRows);
			});
		}
	}

	/**
	 * レイアウトを更新します。
	 */
	private layout(): void {
		let checkable = (this.updateMultiUrl != null) || (this.deleteMultiUrl != null);
	}

	get selectUrl(): string {
		return this.getAttribute('select-url');
	}

	set selectUrl(url: string) {
		this.setAttribute('select-url', url);
		this.datagrid.setAttribute('url', url);
	}

	get insertUrl(): string {
		return this.getAttribute('insert-url');
	}

	get updateUrl(): string {
		return this.getAttribute('update-url');
	}

	get deleteUrl(): string {
		return this.getAttribute('delete-url');
	}

	get updateMultiUrl(): string {
		return this.getAttribute('update-multi-url');
	}

	get deleteMultiUrl(): string {
		return this.getAttribute('delete-multi-url');
	}

	get insertMethod(): string {
		return this.getAttribute('insert-method') || 'POST';
	}

	get updateMethod(): string {
		return this.getAttribute('update-method') || 'PUT';
	}

	get deleteMethod(): string {
		return this.getAttribute('delete-method') || 'DELETE';
	}

}
window.customElements.define('koy-manager', KoyoriManager);


/**
 * 管理対象の定義です。
 */
interface KoyoriManagerDefinition extends KoyoriFormDefinition {

	/** オブジェクトの表示名 */
	label: string;

	/** キーとなるプロパティ名のリスト */
	keys: string[];

	/** 楽観的ロックのためのバージョン管理用プロパティ名 */
	version: string;

	/** 絞り込み可能パラメータ */
	filters: KoyoriFormDefinitionTypeProperty[];

}

